import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  Button, 
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Divider,
  useMediaQuery,
  useTheme,
  TableContainer
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import api from '../services/api';

const Dashboard = () => {
  const { getAccessToken } = useAuth();
  const [stats, setStats] = useState({
    complete: [],
    failed: [],
    in_progress: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    // Format as relative time for recent dates
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffInSeconds < 604800) { // Less than a week
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else {
      // Format as date for older dates
      return date.toLocaleString();
    }
  };
  
  const handleDownload = async (fileName) => {
    try {
      console.log('Downloading file:', fileName);
      setDownloadLoading(fileName);
      const token = await getAccessToken();
      await api.downloadSummary(fileName, token);
      setDownloadLoading(null);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download summary. Please try again.');
      setDownloadLoading(null);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const token = await getAccessToken();
        
        // Get all jobs with large page size to ensure we get everything
        // No filters - we want the full count
        const options = {
          pageSize: 1000, // Large enough to get most or all jobs
        };
        
        const jobs = await api.getJobs('all', token, options);
        
        // In-progress jobs are already filtered correctly on the server,
        // but let's manually count to ensure accuracy
        const completeCount = jobs.complete ? jobs.complete.length : 0;
        const inProgressCount = jobs.in_progress ? jobs.in_progress.length : 0;
        const failedCount = jobs.failed ? jobs.failed.length : 0;
        
        // Update total counts using pagination data if available
        let totalCounts = {
          complete: completeCount,
          in_progress: inProgressCount,
          failed: failedCount
        };
        
        // If pagination info is present, use the total counts
        if (jobs.pagination) {
          // Get individual status counts for each type
          const completeJobs = await api.getJobs('complete', token, { pageSize: 1 });
          const inProgressJobs = await api.getJobs('in_progress', token, { pageSize: 1 });
          const failedJobs = await api.getJobs('failed', token, { pageSize: 1 });
          
          if (completeJobs.pagination) {
            totalCounts.complete = completeJobs.pagination.totalCount;
          }
          
          if (inProgressJobs.pagination) {
            totalCounts.in_progress = inProgressJobs.pagination.totalCount;
          }
          
          if (failedJobs.pagination) {
            totalCounts.failed = failedJobs.pagination.totalCount;
          }
        }
        
        // Keep the first 5 recent completed jobs for the dashboard display
        // but update the counts to accurate numbers
        setStats({
          complete: jobs.complete || [],
          in_progress: jobs.in_progress || [],
          failed: jobs.failed || [],
          counts: totalCounts
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };

    fetchStats();
  }, [getAccessToken]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box 
        display="flex" 
        flexDirection={isMobile ? "column" : "row"} 
        justifyContent="space-between" 
        alignItems={isMobile ? "flex-start" : "center"} 
        mb={4}
        gap={2}
      >
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          component="h1"
          sx={{ mb: isMobile ? 1 : 0 }}
        >
          Dashboard
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CloudUploadIcon />}
          component={RouterLink}
          to="/upload"
          fullWidth={isMobile}
        >
          Upload New File(s)
        </Button>
      </Box>

      <Grid container spacing={2}>
        {/* Use equal column widths on mobile for proper alignment */}
        <Grid item xs={12} sm={4} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, sm: 4, md: 6 },
              pb: { xs: 2.5, sm: 3.5, md: 5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: '100%',
              minHeight: { xs: 180, sm: 200 },
              bgcolor: '#e8f5e9',
              border: '1px solid rgba(76, 175, 80, 0.12)'
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <DescriptionIcon color="success" sx={{ mr: 1 }} />
              <Typography variant={isMobile ? "h5" : "h4"}>Completed</Typography>
            </Box>
            <Typography 
              variant={isMobile ? "h3" : "h2"} 
              sx={{ p: 1, pb: 2, fontWeight: 400, color: '#429e45 !important' }}
            >
              {stats.counts?.complete || (stats.complete ? stats.complete.length : 0)}
            </Typography>
            <Button 
              component={RouterLink} 
              to="/jobs"
              state={{ status: 'complete' }}
              variant="outlined" 
              color="success" 
              sx={{ mt: 'auto', alignSelf: 'center' }}
              fullWidth={isMobile}
            >
              View Completed
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, sm: 4, md: 6 },
              pb: { xs: 2.5, sm: 3.5, md: 5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: '100%',
              minHeight: { xs: 180, sm: 200 },
              bgcolor: '#fff8e1',
              border: '1px solid rgba(255, 152, 0, 0.12)'
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <PendingIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant={isMobile ? "h5" : "h4"}>In Progress</Typography>
            </Box>
            <Typography 
              variant={isMobile ? "h3" : "h2"} 
              sx={{ p: 1, pb: 2, fontWeight: 400, color: '#ffa319 !important' }}
            >
              {stats.counts?.in_progress || (stats.in_progress ? stats.in_progress.length : 0)}
            </Typography>
            <Button 
              component={RouterLink} 
              to="/jobs"
              state={{ status: 'in_progress' }}
              variant="outlined" 
              color="warning" 
              sx={{ mt: 'auto', alignSelf: 'center' }}
              fullWidth={isMobile}
            >
              View In Progress
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, sm: 4, md: 6 },
              pb: { xs: 2.5, sm: 3.5, md: 5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: '100%',
              minHeight: { xs: 180, sm: 200 },
              bgcolor: '#ffebee',
              border: '1px solid rgba(244, 67, 54, 0.12)'
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <ErrorIcon color="error" sx={{ mr: 1 }} />
              <Typography variant={isMobile ? "h5" : "h4"}>Failed</Typography>
            </Box>
            <Typography 
              variant={isMobile ? "h3" : "h2"} 
              sx={{ p: 1, pb: 2, fontWeight: 400, color: '#eb3e31 !important' }}
            >
              {stats.counts?.failed || (stats.failed ? stats.failed.length : 0)}
            </Typography>
            <Button 
              component={RouterLink} 
              to="/jobs"
              state={{ status: 'failed' }}
              variant="outlined" 
              color="error" 
              sx={{ mt: 'auto', alignSelf: 'center' }}
              fullWidth={isMobile}
            >
              View Failed
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {stats.complete && stats.complete.length > 0 && (
        <Box mt={4}>
          <Paper sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8f9fa' }}>
            <Typography 
              variant="h6" 
              mb={3} 
              color="primary"
              sx={{ px: { xs: 1, md: 0 } }}
            >
              Recent Completed Summaries
            </Typography>

            {isMobile ? (
              // Mobile card view for completed summaries
              <Box>
                {[...stats.complete]
                  .sort((a, b) => new Date(b.completion_time) - new Date(a.completion_time))
                  .slice(0, 5)
                  .map((job, index) => (
                    <Card key={index} sx={{ mb: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                      <CardContent sx={{ pb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {job.file_name}
                        </Typography>
                        
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                          <CalendarTodayIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {formatDate(job.start_time)}
                          </Typography>
                        </Box>
                        
                        {job.submitted_by && (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            <PersonIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                            <Typography variant="body2">
                              {job.submitted_by}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                      
                      <Divider />
                      
                      <CardActions>
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          fullWidth
                          startIcon={downloadLoading === job.file_name ? null : <DownloadIcon />}
                          onClick={() => handleDownload(job.file_name)}
                          disabled={downloadLoading === job.file_name}
                        >
                          {downloadLoading === job.file_name ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            'Download'
                          )}
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
              </Box>
            ) : (
              // Desktop table view for completed summaries
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>File Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Submission Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Submitted By</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...stats.complete]
                      .sort((a, b) => new Date(b.completion_time) - new Date(a.completion_time))
                      .slice(0, 5)
                      .map((job, index) => (
                      <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white' } }}>
                        <TableCell>{job.file_name}</TableCell>
                        <TableCell>
                          <Tooltip title={job.start_time ? new Date(job.start_time).toLocaleString() : ''}>
                            <Box component="span">{formatDate(job.start_time)}</Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{job.submitted_by || '-'}</TableCell>
                        <TableCell align="right">
                          <Button 
                            variant="outlined" 
                            size="small"
                            color="primary"
                            startIcon={downloadLoading === job.file_name ? null : <DownloadIcon />}
                            sx={{ ml: 2 }}
                            onClick={() => handleDownload(job.file_name)}
                            disabled={downloadLoading === job.file_name}
                          >
                            {downloadLoading === job.file_name ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              'Download'
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;